import http from "./http";
import { buildUrlFromLocation } from "@/utils/httpUtils";
import { createURI } from "@/utils/string";

export const LADDER_TYPE = {
    simple: 1,
    double: 2
};

export const SIMPLE_LADDER_SIZE = 10;
export const VERITCAL_LADDER_PADDING = 10;

export function deserializeLadder(config) {
    return {
        type: config.type,
        width: config.type === LADDER_TYPE.simple ? SIMPLE_LADDER_SIZE : config.horizontalSpacingBetweenPawls * 4,
        pawlInterval: config.pawlInterval,
        height: config.pawlInterval * config.pawlsCount + VERITCAL_LADDER_PADDING * 2 - config.pawlInterval,
        topPawl: VERITCAL_LADDER_PADDING,
        bottomPawl: VERITCAL_LADDER_PADDING
    };
}

export function serializeLadder(config) {
    return {
        type: config.type,
        horizontalSpacingBetweenPawls: config.width / 4,
        pawlsCount: (config.height - config.topPawl - config.bottomPawl) / config.pawlInterval + 1,
        pawlInterval: config.pawlInterval
    };
}

export const PLANO_STEP = { plano: "elementPlano=1", template: "elementCluster=1" };

export const ELEMENT_AVAILABILITY = {
    carcass: "carcass",
    template: "template",
    planogram: "planogram"
};

export const SYSTEM_TYPE = {
    caddy: 3,
    shelf: 2,
    tile: 1,
    newTile: 4
};

export const ESTORE_AVAILABILITY = {
    unavailable: 1,
    diamOnly: 2,
    everybody: 3
};

export const COMPONENT_TYPE = {
    module: 1,
    product: 2,
    peelOff: 3,
    artwork: 4,
    accessory: 5,
    other: 6,
    structure: 7,
    kit: 8,
    fascia: 11,
    caddy: 12,
    divider: 13,
    shelf: 14,
    ticketStrip: 15,
    frame: 16,
    ladder: 17,
    display: 18,
    blankingGraphic: 19
};

export const COMPONENT_NAME = {
    module: "module",
    product: "product",
    peelOff: "peel-off",
    artwork: "artwork",
    accessory: "accessory",
    other: "other",
    structure: "structure",
    kit: "kit",
    fascia: "fascia",
    blankingGraphic: "blanking_graphic",
    caddy: "caddy",
    divider: "divider",
    shelf: "shelf",
    ticketStrip: "ticket_strip",
    frame: "frame",
    ladder: "ladder",
    display: "display"
};

for (const type in COMPONENT_TYPE) {
    COMPONENT_NAME[COMPONENT_TYPE[type]] = COMPONENT_NAME[type];
}

export default {
    async get(id) {
        console.assert(typeof id === "number");
        const res = await http.get(buildUrlFromLocation(`/components/${id}`));
        return res.data[0];
    },
    async getAll({ brandId, zoneId, zones, campaignId, page, limit, filter, systemTypeId, published, step }) {
        published = published === undefined ? true : published;
        console.assert(typeof page === "number" && page >= 0, "invalid page");
        console.assert(typeof limit === "number" && limit > 0, "invalid limit");
        console.assert(typeof brandId === "number", "missing brandId");
        console.assert(typeof systemTypeId === "number", "missing system type");
        console.assert(typeof zoneId === "number" || (Array.isArray(zones) && zones.length > 0), "missing zones");
        console.assert(typeof campaignId === "number" || campaignId === undefined, "missing campaign");
        console.assert(step === PLANO_STEP.plano || step === PLANO_STEP.template);
        return http.get(
            buildUrlFromLocation(
                createURI("/components", {
                    query: {
                        zoneList: zones && zones.join(","),
                        systemType: systemTypeId,
                        search: filter,
                        limit,
                        offset: page + 1, // Attention, dans le controller de l'API ce qui est appellé 'offset' est en réalité la 'currentPage'
                        notPublished: published ? 0 : 1
                    }
                }) + `&${step}`,
                {
                    brandId,
                    zoneId: zoneId || zones[0],
                    campaignId
                }
            )
        );
    },
    async byType(type, { brandId, zoneId, zones, campaignId, step, published }) {
        published = published === undefined ? true : published;
        console.assert(typeof brandId === "number", "missing brandId");
        console.assert(typeof zoneId === "number" || (Array.isArray(zones) && zones.length > 0), "missing zones");
        console.assert(typeof campaignId === "number" || campaignId === undefined, "missing campaign");
        console.assert(typeof type === "number", "missing type");
        console.assert(step === PLANO_STEP.plano || step === PLANO_STEP.template, "missing step");

        return (await http.get(
            buildUrlFromLocation(
                createURI("/components", {
                    query: {
                        zoneList: zones && zones.join(","),
                        limit: 3000,
                        type,
                        notPublished: published ? 0 : 1
                    }
                }) + `&${step}`,
                {
                    brandId,
                    zoneId: zoneId || zones[0],
                    campaignId
                }
            )
        )).data;
    },
    async fromIds(ids, { brandId, zoneId, campaignId, zones }) {
        console.assert(typeof brandId === "number");
        console.assert(typeof zoneId === "number" || (Array.isArray(zones) && zones.length > 0), "missing zones");
        console.assert(typeof campaignId === "number" || !campaignId);

        console.assert(Array.isArray(ids));
        console.assert(!ids.some(id => typeof id !== "number"));
        return (await http.get(
            buildUrlFromLocation(
                createURI("/components", {
                    query: {
                        ids: ids.join(","),
                        zoneList: zones && zones.join(","),
                        limit: ids.length
                    }
                }),
                {
                    brandId,
                    zoneId: zoneId || zones[0],
                    campaignId
                }
            )
        )).data;
    },
    async getAllResultsWithoutPagination(data) {
        let queryString = "?allResults=1";
        for (let key in data) {
            queryString += "&" + key + "=" + data[key];
        }
        return await http.get(buildUrlFromLocation(`/components${queryString}`));
    },
    async updateConfig(id, config) {
        console.assert(typeof id === "number");
        console.assert(typeof config === "object");

        return await http.put(buildUrlFromLocation(`/components/${id}/config`), JSON.stringify({ content: config }));
    },
    async properties(type) {
        console.assert(Object.values(COMPONENT_TYPE).includes(type));

        const res = await http.get(buildUrlFromLocation(`/components/${type}/property-types`));
        return res;
    },

    async getAllPropertiesType() {
        return await http.get(buildUrlFromLocation(`/admin/property-types`));
    },

    async cogs(id) {
        const res = await http.get(buildUrlFromLocation(`/components/${id}/cogs`));
        return res;
    },

    async exportPdf(componentId) {
        const res = await http.get(buildUrlFromLocation(`/generate-pdf/${componentId}`));
        return res;
    },

    async create(component) {
        return http.post(buildUrlFromLocation("/admin/components"), serializeComponent(component));
    },
    async update(id, component) {
        console.assert(typeof id === "number");

        // const previous = await this.get(id);

        const json = serializeComponent(component);
        // for (const tradKey in previous.terms) {
        //     for (const localeKey in previous.terms[tradKey]) {
        //         const jsonKey = tradKey + localeKey[0].toUpperCase() + localeKey.substr(1).toLowerCase();
        //         // console.log({jsonKey, tradKey, localeKey})
        //         if (json.translations[jsonKey]) json.translations[jsonKey].id = previous.terms[tradKey][localeKey].id;
        //     }
        // }

        // for (const previousCategory of previous.categories) {
        //     const current = json.categories.find((cat) => cat.id === previousCategory.category.id)

        //     console.log('categories', current)
        //     if (current) current.id = previousCategory.id
        // }
        return http.put(buildUrlFromLocation(`/components/${id}`), json);
    }
};

function serializeComponent(component) {
    const translations = [];
    for (const field of ["name", "nickname", "description"]) {
        for (const locale in component[field]) {
            translations.push({
                field,
                locale,
                content: component[field][locale]
            });
        }
    }
    const isAvailableOn = type => {
        return component.selectedAvailableOn.includes(type);
    };

    return {
        visible: component.visible != null ? component.visible : 1,
        toValidate: component.toValidate != null ? component.toValidate : 1,
        isValidate: component.isValidate != null ? component.isValidate : 0,
        status: { id: component.statusId },
        standDesign: { id: component.standDesignId },
        systemTypes: component.selectedSystemTypes.map(systemTypeId => ({ id: systemTypeId })),
        elementCarcass: isAvailableOn(ELEMENT_AVAILABILITY.carcass),
        elementCluster: isAvailableOn(ELEMENT_AVAILABILITY.template),
        elementPlano: isAvailableOn(ELEMENT_AVAILABILITY.planogram),
        eStoreType: component.eStoreData.eStoreType,
        prices: component.eStoreData.prices,
        active: 1,
        name: Object.values(component.name)[0],
        nickname: Object.values(component.nickname)[0],
        description: Object.values(component.description)[0],
        type: {
            id: component.componentType,
            name: COMPONENT_NAME[component.componentType]
        },
        campaign: { id: component.startCampaignId },
        campaignDiscontinued: { id: component.endCampaignId },
        translations,
        categories: component.categories,
        brand: { id: component.brandId },
        properties: Object.keys(component.references).map(id => {
            return { propertyType: { id }, value: component.references[id] };
        }),
        zones: Object.keys(component.zoneDetailsPerId)
            .filter(id => component.zoneDetailsPerId[id].checked)
            .map(id => {
                const details = component.zoneDetailsPerId[id];
                return {
                    zone: { id: Number.parseInt(id, 10) },
                    campaign: { id: details.startCampaignId || component.startCampaignId },
                    discontinuedCampaign: !details.endCampaignId ? undefined : { id: details.endCampaignId }
                };
            })
    };
}



// WEBPACK FOOTER //
// ./src/services/component.js